// QR asset labels, drawn on the iPad. A port of generate_labels_Site10_Baconton.py v4.8
// (safetag_assets.py make_labels, the same design). ASSET_ID_PAGE_WORDS.md section 9.
//
// The script draws a 713 × 285 px PNG at 300 dpi, then places it on a 2.25 × 1.0 in page at
// (0, 0), 2.25 × 0.90 in, leaving a 0.10 in top buffer, then lifts it 0.5 mm (its Y_NUDGE). That lift became
// the QR roll's starting printer offset (printer-offset.ts), which the admin can change on the print card. Here the same geometry is drawn as
// vectors in the label's pixel space and scaled onto the same box, so it prints as sharp as the
// printer allows. Fonts: the script uses Arial from Windows; the PDF standard Helvetica has the same
// widths. A side-by-side check against the script's PNGs is next week's job (section 12).
//
// Toro Max: GP 1 x 3, Scale Default. The 2.25 × 1.0 in label sits centered on a 3 × 1 in page (driver-page.ts,
// QR_ROLL_1X3_WORDS.md rev 2). One label per page.

import { PDFDocument, rgb, StandardFonts, type PDFFont, type PDFPage } from "pdf-lib";
import QRCode from "qrcode";
import { shiftPage, START_OFFSET, type PrinterOffset } from "./printer-offset";
import { driverPage, leadPt } from "./driver-page";

export const LABEL_W = 713;
export const LABEL_H = 285;
const MARGIN = 13;
const QR_SIZE = 250;
const PAGE_W = 2.25 * 72;
const PAGE_H = 1.0 * 72;
const BUFFER_TOP = 0.1 * 72;
/** The driver page the label prints on: GP 1 x 3. */
export const QR_PAGE = driverPage(1, 2.25);
const LEAD = leadPt(2.25, QR_PAGE);
export const FOOTER_LINE1 = "Alltech | Electrical Safety";
export const FOOTER_LINE2 = "Electrical Safety Resources";
export const LABEL_URL = (assetId: string) => `https://getsafetag.com/?aid=${assetId}`;

/**
 * Where PIL puts the baseline below the point it draws from (the ascender line), and how tall it
 * measures the Asset ID, as fractions of the font size. Measured from the script's own drawing.
 */
const ASCENT = 0.92;
const ID_HEIGHT = 0.7;
/** The script's measured line heights: SCAN ME at 44 px, the footer at 25 px. */
const SCAN_LH = 30;
const FOOT_LH = 23;

const black = rgb(0, 0, 0);

/** The QR modules for one label: high error correction, the script's settings. True is a dark module. */
export function qrModules(assetId: string): boolean[][] {
  const q = QRCode.create(LABEL_URL(assetId), { errorCorrectionLevel: "H" });
  const n = q.modules.size;
  const rows: boolean[][] = [];
  for (let y = 0; y < n; y++) {
    const row: boolean[] = [];
    for (let x = 0; x < n; x++) row.push(Boolean(q.modules.get(x, y)));
    rows.push(row);
  }
  return rows;
}

/** The Asset ID's font size: 30 px, shrunk one px at a time until it fits the right column (≥ 11). */
export function idFontSize(assetId: string, width: (text: string, size: number) => number): number {
  const rw = LABEL_W - (MARGIN + QR_SIZE + MARGIN + 10) - MARGIN;
  let size = 30;
  while (size > 10 && width(assetId, size) > rw) size--;
  return size;
}

interface Fonts {
  bold: PDFFont;
  plain: PDFFont;
}

/** Draws one label on a page, in the label's 713 × 285 px space. */
function drawLabel(page: PDFPage, assetId: string, f: Fonts): void {
  const sx = PAGE_W / LABEL_W;
  const sy = (PAGE_H - BUFFER_TOP) / LABEL_H;
  const X = (px: number) => LEAD + px * sx;
  const Y = (py: number) => (LABEL_H - py) * sy;
  const rect = (x: number, y: number, w: number, h: number) => page.drawRectangle({ x: X(x), y: Y(y + h), width: w * sx, height: h * sy, color: black });
  const hline = (x1: number, x2: number, y: number, w: number) => rect(x1, y - w / 2, x2 - x1, w);
  const vline = (x: number, y1: number, y2: number, w: number) => rect(x - w / 2, y1, w, y2 - y1);
  /** Text centered in the right column, its ascender line at `top` (PIL's anchor). */
  const text = (s: string, font: PDFFont, size: number, rx: number, rw: number, top: number) => {
    const w = font.widthOfTextAtSize(s, size);
    page.drawText(s, { x: X(rx + (rw - w) / 2), y: Y(top + ASCENT * size), size: size * sy, font, color: black });
  };

  // Border: 4 px inside the edge.
  rect(0, 0, LABEL_W, 4);
  rect(0, LABEL_H - 4, LABEL_W, 4);
  rect(0, 0, 4, LABEL_H);
  rect(LABEL_W - 4, 0, 4, LABEL_H);

  // QR: border 1 module, scaled to 250 px, centered top to bottom.
  const mods = qrModules(assetId);
  const n = mods.length + 2;
  const cell = QR_SIZE / n;
  const qx = MARGIN;
  const qy = Math.floor((LABEL_H - QR_SIZE) / 2);
  for (let y = 0; y < mods.length; y++) {
    // Runs of dark modules in a row, drawn as one rectangle each.
    let x = 0;
    while (x < mods.length) {
      if (!mods[y]![x]) {
        x++;
        continue;
      }
      const start = x;
      while (x < mods.length && mods[y]![x]) x++;
      rect(qx + (start + 1) * cell, qy + (y + 1) * cell, (x - start) * cell, cell);
    }
  }

  const divX = MARGIN + QR_SIZE + MARGIN;
  vline(divX, MARGIN, LABEL_H - MARGIN, 2);
  const rx = divX + 10;
  const rw = LABEL_W - rx - MARGIN;

  // Asset ID across the top, shrunk to fit.
  const size = idFontSize(assetId, (s, z) => f.bold.widthOfTextAtSize(s, z));
  const idTop = MARGIN + 14;
  text(assetId, f.bold, size, rx, rw, idTop);
  const y1 = idTop + Math.round(ID_HEIGHT * size) + 20;
  hline(rx, LABEL_W - MARGIN, y1, 1);

  // SCAN ME / FIRST between the rules.
  const y2 = LABEL_H - MARGIN - 66;
  const lh = SCAN_LH;
  let y = y1 + Math.floor((y2 - y1 - (lh * 2 + 10)) / 2);
  for (const t of ["SCAN ME", "FIRST"]) {
    text(t, f.bold, 44, rx, rw, y);
    y += lh + 10;
  }
  hline(rx, LABEL_W - MARGIN, y2, 1);

  // Footer, two lines.
  const fh = FOOT_LH;
  y = y2 + Math.floor((LABEL_H - MARGIN - y2 - (fh * 2 + 6)) / 2);
  for (const t of [FOOTER_LINE1, FOOTER_LINE2]) {
    text(t, f.plain, 25, rx, rw, y);
    y += fh + 6;
  }
}

/** One page per label (the 2.25 × 1.0 in label centered on GP 1 x 3), in the order given, moved by this iPad's QR roll offset. */
export async function qrLabelsPdf(assetIds: readonly string[], offset: PrinterOffset = START_OFFSET.qrRoll): Promise<Uint8Array> {
  const pdf = await PDFDocument.create();
  pdf.setTitle("SafeTag QR Labels");
  pdf.setCreator("SafeTag Asset ID page");
  const fonts: Fonts = { bold: await pdf.embedFont(StandardFonts.HelveticaBold), plain: await pdf.embedFont(StandardFonts.Helvetica) };
  for (const id of assetIds) {
    const page = pdf.addPage([QR_PAGE.length * 72, PAGE_H]);
    drawLabel(page, id, fonts);
    shiftPage(page, offset);
  }
  return pdf.save();
}

/** "SafeTag_QR_Labels_Site10_Baconton_2026-10-22.pdf". */
export function qrPdfName(siteNumber: string, siteName: string, isoNow: string): string {
  return `SafeTag_QR_Labels_Site${siteNumber}_${siteName.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "")}_${isoNow.slice(0, 10)}.pdf`;
}
